import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Globe, Clock, Shield, CalendarDays, Plane, ArrowRight,
  Search, FileText, Loader2
} from 'lucide-react';
import { visaTypesApi, countriesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import CountryFlag from '../components/CountryFlag';

const VisaTypesPage = () => {
  const { user } = useAuth();
  const [visaTypes, setVisaTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [visaRes, countryRes] = await Promise.all([
          visaTypesApi.getAll(),
          countriesApi.getAll()
        ]);
        setVisaTypes(visaRes.data);
        setCountries(countryRes.data);
      } catch (error) {
        console.error('Error fetching visa types:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getCountry = (countryId) => countries.find(c => c.country_id === countryId);

  const filtered = visaTypes.filter(v => {
    const country = getCountry(v.country_id);
    const matchesSearch = !search ||
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      country?.name?.toLowerCase().includes(search.toLowerCase());
    const matchesCountry = filterCountry === 'all' || v.country_id === filterCountry;
    return matchesSearch && matchesCountry;
  });

  // Group by country
  const grouped = filtered.reduce((acc, visa) => {
    const key = visa.country_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(visa);
    return acc;
  }, {});

  const countryIds = Object.keys(grouped);

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar />
      <main className="pt-24 pb-16 px-4" data-testid="visa-types-page">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0A1E40] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Visa Types
            </h1>
            <p className="text-[#4A5568] text-base">Browse all available visa types across countries</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-[#D1CBBF] p-4 mb-8">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder="Search visa types or countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#F5F0E6] border-[#E8E2D6] h-11"
                data-testid="visa-search"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setFilterCountry('all')}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterCountry === 'all'
                    ? 'bg-[#FF5A36] text-white'
                    : 'bg-[#F5F0E6] text-[#4A5568] hover:bg-[#E8E2D6]'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {countries.map(c => (
                <button
                  key={c.country_id}
                  onClick={() => setFilterCountry(c.country_id)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filterCountry === c.country_id
                      ? 'bg-[#FF5A36] text-white'
                      : 'bg-[#F5F0E6] text-[#4A5568] hover:bg-[#E8E2D6]'
                  }`}
                  data-testid={`filter-${c.country_id}`}
                >
                  <CountryFlag code={c.code} size="sm" className="mr-1" /> {c.name}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36]" />
            </div>
          ) : countryIds.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#D1CBBF]">
              <Globe className="w-12 h-12 text-[#D1CBBF] mx-auto mb-3" />
              <p className="text-[#4A5568]">No visa types found</p>
            </div>
          ) : (
            <div className="space-y-10">
              {countryIds.map(countryId => {
                const country = getCountry(countryId);
                if (!country) return null;
                const visas = grouped[countryId];
                const slug = country.code?.toLowerCase();

                return (
                  <div key={countryId} data-testid={`country-group-${countryId}`}>
                    {/* Country Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <CountryFlag code={country.code} size="xl" />
                      <div>
                        <Link to={`/countries/${slug}`} className="hover:underline">
                          <h2 className="text-xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {country.name}
                          </h2>
                        </Link>
                        <p className="text-xs text-[#4A5568]">{visas.length} visa type{visas.length > 1 ? 's' : ''} available</p>
                      </div>
                    </div>

                    {/* Visa Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {visas.map(visa => (
                        <div
                          key={visa.visa_type_id}
                          className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-lg transition-all"
                          data-testid={`visa-card-${visa.visa_type_id}`}
                        >
                          <div className="p-5">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex items-center gap-2">
                                <Plane className="w-4 h-4 text-[#FF5A36]" />
                                <h3 className="font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                  {visa.name}
                                </h3>
                              </div>
                              <p className="text-lg font-bold text-[#0A1E40] shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {visa.fee?.toLocaleString('en-IN', { style: 'currency', currency: visa.currency || 'INR', maximumFractionDigits: 0 })}
                              </p>
                            </div>
                            {visa.description && (
                              <p className="text-sm text-[#4A5568] mb-3">{visa.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <span className="inline-flex items-center gap-1 text-xs bg-[#F5F0E6] rounded-lg px-2.5 py-1.5">
                                <Clock className="w-3 h-3 text-[#FF5A36]" />
                                <span className="text-[#4A5568]">{visa.processing_time}</span>
                              </span>
                              <span className="inline-flex items-center gap-1 text-xs bg-[#F5F0E6] rounded-lg px-2.5 py-1.5">
                                <Shield className="w-3 h-3 text-[#FF5A36]" />
                                <span className="text-[#4A5568]">{visa.validity}</span>
                              </span>
                              <span className="inline-flex items-center gap-1 text-xs bg-[#F5F0E6] rounded-lg px-2.5 py-1.5">
                                <CalendarDays className="w-3 h-3 text-[#FF5A36]" />
                                <span className="text-[#4A5568]">{visa.max_stay}</span>
                              </span>
                            </div>
                            {visa.documents_required?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {visa.documents_required.slice(0, 3).map((doc, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded text-[11px] text-blue-700">
                                    <FileText className="w-3 h-3" /> {doc}
                                  </span>
                                ))}
                                {visa.documents_required.length > 3 && (
                                  <span className="text-[11px] text-[#9CA3AF] self-center">+{visa.documents_required.length - 3} more</span>
                                )}
                              </div>
                            )}
                            <Link
                              to={user ? `/apply?country=${countryId}&visa=${visa.visa_type_id}` : '/login'}
                              state={!user ? { from: `/apply?country=${countryId}&visa=${visa.visa_type_id}` } : undefined}
                            >
                              <Button className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl h-10 text-sm font-semibold" data-testid={`apply-${visa.visa_type_id}`}>
                                Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VisaTypesPage;
